import { useCallback, useEffect, useRef, useState } from "react";
import { microAppService } from "@/lib/services";
import type { MicroAppBindingData } from "@/types";
import { extractBindingKeys } from "./hydrationRuntime";

interface UseDashboardHydrationArgs {
  uuid: string | undefined;
  html: string;
  iframeRef: React.RefObject<HTMLIFrameElement | null>;
}

interface DashboardHydrationState {
  /** Whether this app has any live bindings (drives the "Live" badge). */
  isLive: boolean;
  /** Timestamp hydration last completed, or null before first hydrate. */
  lastUpdated: Date | null;
  /** True while binding data is being fetched. */
  refreshing: boolean;
  /** Number of bindings whose last fetch failed (served stale/last-known). */
  errorCount: number;
  /** Re-fetch all bindings and push them into the iframe. */
  refresh: () => void;
}

const READY = "micro-app:ready";
const HYDRATED = "micro-app:hydrated";
const HYDRATE = "micro-app:hydrate";

/**
 * Drives live-data hydration for a dashboard micro app.
 *
 * The iframe is opaque-origin, so the parent cannot reach into it. Instead:
 *  1. Parent parses the bindings manifest to learn which keys exist.
 *  2. Parent fetches each binding's data (with the user's token).
 *  3. The injected runtime posts `micro-app:ready` when it can receive data.
 *  4. Parent posts `{ type: "micro-app:hydrate", bindings }` into the iframe.
 *  5. Runtime hydrates the cells and posts `micro-app:hydrated` back.
 */
export function useDashboardHydration({ uuid, html, iframeRef }: UseDashboardHydrationArgs): DashboardHydrationState {
  const [lastUpdated, setLastUpdated] = useState<Date | null>(null);
  const [refreshing, setRefreshing] = useState(false);
  const [errorCount, setErrorCount] = useState(0);
  const bindingKeys = html ? extractBindingKeys(html) : [];
  const isLive = bindingKeys.length > 0;

  // Latest fetched payload, kept so we can (re)post whenever the iframe runtime
  // announces readiness (it re-announces until it receives data).
  const payloadRef = useRef<Record<string, MicroAppBindingData> | null>(null);
  const keysRef = useRef<string[]>(bindingKeys);
  keysRef.current = bindingKeys;
  // Monotonic token identifying the current fetch generation. Bumped on every
  // refresh and on app/document change; a resolved fetch whose token no longer
  // matches is stale (superseded) and its result is dropped — this prevents a
  // slow response from a previous app/refresh clobbering newer data.
  const requestTokenRef = useRef(0);
  // Aborts superseded fetches so cancelled refreshes stop consuming backend
  // work (each binding is a real SQL/Graph query server-side).
  const abortRef = useRef<AbortController | null>(null);
  // Guard against setState after unmount (rapid navigation away).
  const mountedRef = useRef(true);
  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      abortRef.current?.abort();
    };
  }, []);

  const postToIframe = useCallback(
    (bindings: Record<string, MicroAppBindingData>) => {
      const win = iframeRef.current?.contentWindow;
      if (win) win.postMessage({ type: HYDRATE, bindings }, "*");
    },
    [iframeRef],
  );

  const refresh = useCallback(async () => {
    if (!uuid || keysRef.current.length === 0) return;
    const token = ++requestTokenRef.current;
    abortRef.current?.abort(); // cancel any superseded fetch
    const controller = new AbortController();
    abortRef.current = controller;
    setRefreshing(true);
    try {
      const results = await Promise.all(
        keysRef.current.map(async (key): Promise<[string, MicroAppBindingData]> => {
          try {
            const res = await microAppService.getBindingData(uuid, key, controller.signal);
            return [key, res.data];
          } catch {
            return [key, { bindingKey: key, value: null, executedAt: null, cached: false, error: "load_failed" }];
          }
        }),
      );
      // Drop the result if a newer refresh (or app switch) has since started, or
      // if we've unmounted — never post stale data into a reused iframe.
      if (token !== requestTokenRef.current || !mountedRef.current) return;
      const map = Object.fromEntries(results) as Record<string, MicroAppBindingData>;
      payloadRef.current = map;
      // Count bindings that failed to refresh (they still show last-known value).
      setErrorCount(Object.values(map).filter((b) => b.error != null).length);
      // Post immediately. If the iframe listener isn't up yet, the runtime keeps
      // re-announcing "ready", and the message handler below re-posts this
      // payload each time — so the data lands regardless of ordering.
      postToIframe(map);
    } finally {
      if (token === requestTokenRef.current && mountedRef.current) setRefreshing(false);
    }
  }, [uuid, postToIframe]);

  // Listen for the iframe runtime's lifecycle messages.
  useEffect(() => {
    function onMessage(event: MessageEvent) {
      // srcDoc iframes have an opaque origin ("null"); reject anything else.
      if (event.origin !== "null") return;
      // Only accept messages originating from our iframe's live window.
      const iframeWin = iframeRef.current?.contentWindow;
      if (!iframeWin || event.source !== iframeWin) return;
      const data = event.data;
      if (!data || typeof data !== "object") return;
      if (data.type === READY) {
        // Runtime is (re)announcing it can receive data. If we already have a
        // payload, (re)post it — this covers the case where our first post
        // raced ahead of the iframe's listener.
        if (payloadRef.current) postToIframe(payloadRef.current);
      } else if (data.type === HYDRATED) {
        setLastUpdated(new Date());
      }
    }
    window.addEventListener("message", onMessage);
    return () => window.removeEventListener("message", onMessage);
  }, [iframeRef, postToIframe]);

  // Clear buffered state and invalidate any in-flight fetch when a fresh
  // document loads (new app / reload), so a slow prior fetch can't post here.
  useEffect(() => {
    requestTokenRef.current++;
    abortRef.current?.abort();
    payloadRef.current = null;
    setErrorCount(0);
  }, [html]);

  // Kick off the initial fetch once we know the app is live.
  useEffect(() => {
    if (isLive) refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [uuid, isLive, html]);

  return { isLive, lastUpdated, refreshing, errorCount, refresh };
}
